import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Tab } from 'src/app/core/interfaces/tab';
import { EnvironmentPropsService } from 'src/app/core/services/environment-props/environment-props.service';
import { MapsService } from 'src/app/core/services/simulation/objects/maps/maps.service';

@Component({
  selector: 'app-label-tabs',
  templateUrl: './label-tabs.component.html',
  styleUrls: ['./label-tabs.component.scss'],
})
export class LabelTabsComponent implements OnChanges {
  @Input() items: Tab[];
  @Input() currentTabId: Tab;
  @Input() objectName: string;
  @Input() taller = false;
  @Input() searchable = true;
  @Input() truncate = true;
  @Input() baseRoute = '';

  @Output() selected = new EventEmitter<Tab>();
  @Output() opened = new EventEmitter<boolean>();

  openingState: boolean;
  filteredTabs: Tab[];
  tabs: Tab[];
  query: string;
  fieldLabel: string;
  placeholder: string;
  titleReference: string;

  currentTab: Tab;

  constructor(
    private mapsService: MapsService,
    private environmentPropsService: EnvironmentPropsService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.fieldLabel = 'Label ';
    this.placeholder = 'Search registered ';
    this.titleReference = 'label';
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setTabs();
    this.setFilteredTabs();

    if (changes.objectName) {
      this.resetFieldString();
    }

    if (changes.items?.firstChange && this.tabs?.length) {
      this.selectTab(this.currentTabId || this.tabs[0].id);
    }

    if (changes.currentTabId) {
      this.selectItem(this.currentTabId);
    }

    this.setDuplicatedStates();
  }

  setFilteredTabs(): void {
    this.filteredTabs = this.items;
  }

  setTabs(): void {
    if (this.tabs) {
      this.clearRemovedTabs();
      if (this.items?.length >= 5) {
        this.tabs = this.tabs.map((tab) => {
          return this.items?.find((item) => item.id === tab.id);
        });
        this.pushToCompleteTabs();
      } else {
        this.tabs = this.items;
      }
    } else {
      this.tabs = this.items?.filter((item, index) => index < 5);
    }
  }

  clearRemovedTabs() {
    this.tabs = this.tabs.filter((tab) => {
      return this.items.find((item) => item.id === tab.id);
    });
  }

  pushToCompleteTabs() {
    if (this.tabs.length < 5 && this.items?.length >= 5) {
      this.items?.forEach((item) => {
        if (this.tabs.length === 5) {
          return;
        } else {
          const alreadyBetweenTabs = this.tabs.find((tab) => tab.id === item.id);
          if (!alreadyBetweenTabs) {
            this.tabs.push(item);
          }
        }
      });
    }
  }

  setDuplicatedStates(): void {
    this.items.forEach((item) => {
      item.duplicated = this.mapsService.isDuplicatedTitle(item.label);
    });
  }

  getValidLabel(tab: { id: any; label: string }): string {
    return String(tab.label) ? tab.label : `${this.objectName}${tab.id}`;
  }

  resetFieldString(): void {
    if (this.objectName) {
      this.fieldLabel += this.objectName;
      this.placeholder += this.objectName;
    }
  }

  open(): void {
    this.openingState = true;
    this.opened.emit(true);
  }

  close(): void {
    this.openingState = false;
    this.opened.emit(false);
  }

  search(): void {
    const queryStr = String(this.query);
    if (queryStr === '') {
      this.filteredTabs = this.items;
    } else {
      this.filteredTabs = this.items?.filter(({ label }) => {
        const parsedLabel = String(label).trim().toLowerCase();
        const parsedQuery = queryStr.trim().toLowerCase();
        return parsedLabel.includes(parsedQuery);
      });
    }
  }

  navigate(route: string[]): void {
    if (route.includes(null)) {
      return;
    }
    this.router.navigate(route, { relativeTo: this.route });
  }

  selectItem(id: any): void {
    const selected = this.items?.find((label) => label.id === id);
    if (!selected) {
      return;
    }

    const newSelection = !this.tabs?.find((label) => label.id === id);
    if (newSelection) {
      if (this.tabs.length === 5) {
        this.tabs.pop();
      }
      this.tabs.unshift(selected);
    }
    this.selectTab(id);
  }

  selectTab(id: any): void {
    this.currentTab = this.items?.find((label) => label.id === id);
    if (this.currentTab?.id !== this.currentTabId) {
      this.navigate(this.baseRoute ? [this.baseRoute, id] : [id]);
      this.selected.emit(this.currentTab);
    }
    this.close();
  }

  get currentTabList(): Array<{ id: any; label: string }> {
    return this.tabs;
  }

  get isAnflex(): boolean {
    return this.environmentPropsService.isAnflex;
  }
}
